import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export const LEARNING_TOGGLE = "learning_toggle";
// A learning_toggle with no command_result after this long is treated as lost.
export const LEARNING_REPLY_TIMEOUT_MS = 8000;

const STATUS_APPLIED = "applied";

/** The `enabled` of the last learning_note block in replayed turns; undefined when there is none. */
export function lastLearningNote(turns) {
  if (!Array.isArray(turns)) return undefined;
  for (let t = turns.length - 1; t >= 0; t -= 1) {
    const blocks = Array.isArray(turns[t]?.blocks) ? turns[t].blocks : [];
    for (let b = blocks.length - 1; b >= 0; b -= 1) {
      if (blocks[b]?.t === "learning_note") return blocks[b].enabled !== false;
    }
  }
  return undefined;
}

/**
 * The session learning switch (final plan N12): per session, on by default, kept server-side in the case file.
 *
 * Shows only server-confirmed values, newest first: the tse:joined ack (`learning_enabled`), each accepted
 * diag_state (`learning.enabled`, while a diagnosis is open), a live learning note, and an applied
 * command_result for our own request. The last learning_note of the replay seeds it only when the ack carried
 * no value. A request stays pending (the switch is disabled) until its command_result, or until the reply
 * timeout; rejected / ignored / lost leaves the shown value untouched.
 *
 *   begin(enabled)  after sendCommand succeeded; false while a request is already outstanding.
 *   onResult        tse:command_result (any command; others are ignored).
 *   abandon         drop the outstanding request (timeout, disconnect, clear).
 */
export default function useSessionLearning({ sessionId, diagState, markPending, clearPending }) {
  const [known, setKnown] = useState({ sid: null, enabled: undefined });
  const sessionIdRef = useRef(sessionId);
  sessionIdRef.current = sessionId;
  const requestedRef = useRef(null);
  const timerRef = useRef(null);
  const ackSidRef = useRef(null);

  const adopt = useCallback((sid, enabled) => {
    if (typeof enabled !== "boolean") return;
    setKnown({ sid: String(sid || sessionIdRef.current || ""), enabled });
  }, []);

  const clearTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);

  const abandon = useCallback(() => {
    clearTimer();
    if (requestedRef.current === null) return;
    requestedRef.current = null;
    clearPending(LEARNING_TOGGLE);
  }, [clearTimer, clearPending]);

  // Identity, not value: every accepted diag_state re-states the server's value, even an unchanged one.
  useEffect(() => {
    adopt(diagState?.session_id, diagState?.learning?.enabled);
  }, [diagState, adopt]);

  const isPending = useCallback(() => requestedRef.current !== null, []);

  const begin = useCallback((enabled) => {
    if (requestedRef.current !== null) return false;
    requestedRef.current = !!enabled;
    markPending(LEARNING_TOGGLE);
    clearTimer();
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      abandon();
    }, LEARNING_REPLY_TIMEOUT_MS);
    return true;
  }, [markPending, clearTimer, abandon]);

  const onResult = useCallback((payload) => {
    if (payload?.command !== LEARNING_TOGGLE) return;
    const requested = requestedRef.current;
    requestedRef.current = null;
    clearTimer();
    if (payload.status === STATUS_APPLIED && requested !== null) adopt(payload.session_id, requested);
  }, [clearTimer, adopt]);

  const seedFromJoin = useCallback((payload) => {
    if (typeof payload?.learning_enabled !== "boolean") return;
    const sid = String(payload.session_id || sessionIdRef.current || "");
    ackSidRef.current = sid;
    adopt(sid, payload.learning_enabled);
  }, [adopt]);

  const seedFromReplay = useCallback((payload) => {
    const sid = String(payload?.session_id || sessionIdRef.current || "");
    if (ackSidRef.current === sid) return;
    adopt(sid, lastLearningNote(payload?.turns));
  }, [adopt]);

  const noteServerValue = useCallback((sid, enabled) => adopt(sid, enabled), [adopt]);

  useEffect(() => clearTimer, [clearTimer]);

  const current = known.sid && known.sid === String(sessionId || "") ? known.enabled : undefined;
  const enabled = current !== false;

  return useMemo(
    () => ({ enabled, isPending, begin, onResult, abandon, seedFromJoin, seedFromReplay, noteServerValue }),
    [enabled, isPending, begin, onResult, abandon, seedFromJoin, seedFromReplay, noteServerValue],
  );
}
